import type { FC, MouseEvent } from "react";

export interface ToggleSwitchProps {
  checked?: boolean;
  onChange: () => void;
  /** Active (on) background color class. Defaults to "bg-brand". */
  activeColorClass?: string;
  /** Inactive (off) track colors. */
  inactiveColorClass?: string;
  /**
   * Focus/spacing modifier classes inserted after the shared base classes and
   * before the active/inactive color. Defaults to the order-form settings
   * variant. This is what differentiates the visual variants while keeping the
   * track/knob markup shared.
   */
  trackModifierClassName?: string;
  /** Render as a clickable div instead of a button (no `type`). */
  as?: "button" | "div";
  /** Call e.preventDefault() before onChange (used inside forms). */
  preventDefault?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  tabIndex?: number;
}

/**
 * Custom (non-flowbite) toggle switch. Consolidates the toggle markup that was
 * duplicated inline across the order-form settings tabs, CompanyModal,
 * ToggleCard/SimpleToggle and the category lists. Variants are expressed via
 * props so each previous call site renders byte-identical output.
 */
export const ToggleSwitch: FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  activeColorClass = "bg-brand",
  inactiveColorClass = "bg-surface-muted border-border-strong",
  trackModifierClassName = "focus:ring-2 focus:ring-brand focus:ring-offset-2",
  as = "button",
  preventDefault = false,
  ariaLabel,
  ariaLabelledBy,
  tabIndex,
}) => {
  const trackClassName = `relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none ${trackModifierClassName} ${
    checked
      ? `${activeColorClass} border-transparent`
      : inactiveColorClass
  }`;

  const knob = (
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  );

  const handleClick = (e: MouseEvent) => {
    if (preventDefault) e.preventDefault();
    onChange();
  };

  if (as === "div") {
    return (
      <div
        className={trackClassName}
        onClick={handleClick}
        role="switch"
        aria-checked={checked}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {knob}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={trackClassName}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {knob}
    </button>
  );
};
